export const deploymentStates = Object.freeze({
    STOPPED: 'STOPPED',
    STARTING: 'STARTING',
    RUNNING: 'RUNNING',
    STOPPING: 'STOPPING',
    UNAVAILABLE: 'UNAVAILABLE'
})

// A missing Deployment is the only stopped state. Kubernetes may temporarily
// report status.replicas=0/availableReplicas=0 while the Deployment and session
// still exist (for example after a hard container failure). That state must stay
// visible and stoppable rather than being presented as a new start opportunity.
export function deploymentState(deployment) {
    if (!deployment) return deploymentStates.STOPPED

    const replicas = Number(deployment.replicas)
    const available = Number(deployment.available_replicas)
    if (!Number.isFinite(replicas) || !Number.isFinite(available)) {
        return deploymentStates.UNAVAILABLE
    }
    if (replicas > 0 && replicas === available) {
        return deploymentStates.RUNNING
    }
    if (replicas > available) {
        return deploymentStates.STARTING
    }
    if (replicas === 0 && available === 0) {
        return deploymentStates.UNAVAILABLE
    }
    return deploymentStates.STOPPING
}

export function deploymentStatusInfo(deployment) {
    const state = deploymentState(deployment)
    if (deploymentHasFailure(deployment) && state !== deploymentStates.RUNNING) {
        return {
            state: deploymentStates.UNAVAILABLE,
            label: deploymentStatusLabel(deployment, 'Unavailable'),
            color: 'error',
            pulse: false
        }
    }
    switch (state) {
        case deploymentStates.RUNNING:
            return { state, label: 'Running', color: 'success', pulse: false }
        case deploymentStates.STARTING:
            return {
                state,
                label: deploymentStatusLabel(deployment, 'Starting…') || 'Starting…',
                color: 'deep-orange',
                pulse: true
            }
        case deploymentStates.STOPPING:
            return { state, label: 'Stopping…', color: 'blue-grey', pulse: true }
        case deploymentStates.UNAVAILABLE:
            return {
                state,
                label: deploymentStatusLabel(deployment, 'Unavailable') || 'Unavailable',
                color: 'error',
                pulse: false
            }
        default:
            return { state, label: '', color: '', pulse: false }
    }
}

/** Waiting/terminated reasons that mean the app is unhealthy, not merely starting. */
export const deploymentFailureReasons = Object.freeze(
    new Set([
        'OOMKilled',
        'CrashLoopBackOff',
        'ImagePullBackOff',
        'ErrImagePull',
        'Evicted',
        'Failed',
        'CreateContainerConfigError',
        'CreateContainerError',
        'InvalidImageName',
        'RunContainerError',
        'Error',
        'Completed' // completed-but-not-ready often pairs with crashloop lastState
    ])
)

/**
 * True when app_status reports a concrete container/pod failure.
 * Replica math alone would still say STARTING for CrashLoopBackOff.
 */
export function deploymentHasFailure(deployment) {
    if (!deployment || !deployment.failure_reason) return false
    return deploymentFailureReasons.has(deployment.failure_reason)
}

/**
 * User-facing status line for the applications list / chips.
 * Prefer k8s reason + restart count over the coarse Starting label.
 */
export function deploymentStatusLabel(deployment, coarseStatus) {
    if (!deployment) return coarseStatus || ''
    const reason = deployment.failure_reason
    const restarts = Number(deployment.restart_count)
    const restartPart = Number.isFinite(restarts) && restarts > 0 ? ` · ${restarts} restart${restarts === 1 ? '' : 's'}` : ''
    if (reason && deploymentHasFailure(deployment)) {
        return `${reason}${restartPart}`
    }
    if (restartPart && coarseStatus && coarseStatus !== 'Running' && coarseStatus !== 'Stopped') {
        return `${coarseStatus}${restartPart}`
    }
    return coarseStatus || ''
}
